import {
  createMetricFromResponse, createServerModelFromMetric, type MetricModel,
} from '~/_shared/utils/metric/metrics.factory';
import {
  createEtagFromServerModal, createEtagServerModel,
} from '~/boundary/etag/etag.factory';
import { noDataBoundaryTerritoryIds } from '~/boundary/settings/boundarySettings.helpers';
import {
  defaultBoundaryTerritoryGroupResponseSettings, defaultBoundaryTerritoryGroupSettings, getDefaultLabelValueSettings,
} from '~/boundary/settings/defaultBoundarySettings';
import { boundaryTerritoryGroupCreateSettingsStyleDefaults } from '~/boundary/settings/defaultBoundaryTerritoryStyles';
import { getCalculateBucketFunction } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryTerritoryGroup.helpers';
import { ChartMetricType } from '../mapSettings/data/charts/mapSettingsCharts.types';
import {
  BoundaryMatchingType,
  type BoundaryTerritoryGroupConvertRequest, type BoundaryTerritoryGroupCreateRequest,
  type BoundaryTerritoryGroupDefaultStatisticServerModel,
  type BoundaryTerritoryGroupMatchingsRequest, type BoundaryTerritoryGroupMatchingsResponse,
  type BoundaryTerritoryGroupModelResponse, type BoundaryTerritoryGroupResponse,
  type BoundaryTerritoryGroupSettingsRequest, type BoundaryTerritoryGroupSettingsResponse,
  type BoundaryTerritoryGroupUpdateRequest, type BoundaryTerritoryLabelStyleServerModel,
  type BoundaryTerritoryLabelValueConfigServerModel, type BoundaryTerritoryRequest,
  type BoundaryTerritoryResponse,
  type BoundaryTerritoryStyleRequest,
  type BoundaryTerritoryStyleResponse, type MetricServerModel,
} from './boundaryTerritoryGroups.repository';
import {
  type BoundaryTerritory, type BoundaryTerritoryGroup, type BoundaryTerritoryGroupDefaultStatistic,
  type BoundaryTerritoryGroupMatchings,
  type BoundaryTerritoryGroupModel, type BoundaryTerritoryGroupSettings, type BoundaryTerritoryGroupStyle,
  type BoundaryTerritoryLabelStyle,
  type BoundaryTerritoryLabelValueConfig, type BoundaryTerritoryStyle,
} from './boundaryTerritoryGroups.state';

export const getBoundaryTerritoryGroupMatchupRequest = (
  request: BoundaryTerritoryGroupMatchings
): BoundaryTerritoryGroupMatchingsRequest => {
  return {
    matching_type: request.matchingType,
    match_columns: request.matchColumns.map(column => ({
      column_id: column.columnId,
      category: column.category,
    })),
  };
};

export const createTerritoryBoundaryMatchingsFromResponse = (
  response: BoundaryTerritoryGroupMatchingsResponse
): BoundaryTerritoryGroupMatchings => {
  return {
    matchingType: response.matching_type,
    matchColumns: response.match_columns.map(column => ({
      columnId: column.column_id,
      category: column.category,
    })),
  };
};

export const createTerritoryBoundaryGroupFromResponse = (
  response: BoundaryTerritoryGroupResponse
): BoundaryTerritoryGroup => {
  return {
    boundaryGroupId: response.boundary_group_id,
    boundaryTerritoryGroupId: response.boundary_territory_group_id,
    archivedAt: response.archived_at,
    mapId: response.map_id,
    matchings: createTerritoryBoundaryMatchingsFromResponse(response.matchings),
    settings: createBoundaryTerritoryGroupSettingsFromResponse(response.settings),
    etag: response.etag ? createEtagFromServerModal(response.etag) : undefined,
  };
};

export const createTerritoryBoundaryGroupModelFromResponse = (
  response: BoundaryTerritoryGroupModelResponse
): BoundaryTerritoryGroupModel => {
  return {
    boundaryGroupId: response.boundary_group_id,
    boundaryTerritoryGroupId: response.boundary_territory_group_id,
    archivedAt: response.archived_at,
    mapId: response.map_id,
    matchings: createTerritoryBoundaryMatchingsFromResponse(response.matchings),
    settings: createBoundaryTerritoryGroupSettingsFromResponse(response.settings),
    etag: response.etag ? createEtagFromServerModal(response.etag) : undefined,
  };
};

const convertLabelConfigToServerModel = (labelConfig: BoundaryTerritoryLabelValueConfig): BoundaryTerritoryLabelValueConfigServerModel => {
  return {
    demographic_id: labelConfig.labelValueDemographicId,
    include_legend: labelConfig.includeLegend,
    is_value_custom: labelConfig.isValueCustom,
    label_value_aggregation_function: labelConfig.labelValueAggregationFunction,
    label_value_column_id: labelConfig.labelValueColumnId,
    label_value_type: labelConfig.labelValueType,
    show_value: labelConfig.showValue,
  };
};

const convertLabelStyleToServerModel = (labelStyle: BoundaryTerritoryLabelStyle): BoundaryTerritoryLabelStyleServerModel => {
  return {
    font_color: labelStyle.fontColor,
    background_color: labelStyle.backgroundColor,
  };
};

export const createRequestFromBoundaryTerritoryGroupSettings = (
  settings: BoundaryTerritoryGroupSettings
): BoundaryTerritoryGroupSettingsRequest => {
  return {
    allow_decimal_ranges: settings.allowDecimalRanges,
    boundaries_as_territories: settings.boundariesAsTerritories,
    boundary_fill_type: settings.boundaryFillType,
    boundary_style: getBoundaryTerritorySettingsBoundaryStyleRequest(settings.boundaryStyle),
    boundary_territories: settings.boundaryTerritories.map(territory => createBoundaryTerritoryRequest(territory)),
    boundary_territory_type: settings.boundaryTerritoryType,
    bucket_column_id: settings.bucketColumnId,
    bucket_type: settings.bucketType,
    bucket_distribution: settings.bucketDistribution,
    calculate_bucket_function: settings.calculateBucketFunction,
    demographic_id: settings.demographicId,
    metrics: settings.metrics.map(createServerModelFromMetric).filter((metric): metric is MetricServerModel => !!metric),
    style: createBoundaryTerritoryStyleRequest(settings.style),
    default_statistics: settings.defaultStatistics?.map(item => createBoundaryTerritoryDefaultStatisticRequest(item)),
    boundary_territory_label_config: convertLabelConfigToServerModel(settings.labelValueConfig),
    boundary_territory_label_style: convertLabelStyleToServerModel(settings.labelStyle),
  };
};

const convertLabelConfigFromServerModel = (serverModel: BoundaryTerritoryLabelValueConfigServerModel): BoundaryTerritoryLabelValueConfig => {
  return {
    labelValueDemographicId: serverModel.demographic_id,
    includeLegend: serverModel.include_legend,
    isValueCustom: serverModel.is_value_custom,
    labelValueAggregationFunction: serverModel.label_value_aggregation_function,
    labelValueColumnId: serverModel.label_value_column_id,
    labelValueType: serverModel.label_value_type,
    showValue: serverModel.show_value,
  };
};

const convertLabelStyleFromServerModel = (serverModel: BoundaryTerritoryLabelStyleServerModel): BoundaryTerritoryLabelStyle => {
  return {
    backgroundColor: serverModel.background_color,
    fontColor: serverModel.font_color,
  };
};

const createBoundaryTerritoryGroupSettingsFromResponse = (
  settings: BoundaryTerritoryGroupSettingsResponse
): BoundaryTerritoryGroupSettings => {
  const boundaryTerritories = settings.boundary_territories
    .map(item => createBoundaryTerritoryFromResponse(item))
    .sort(sortBoundaryTerritoriesFromResponse);

  const hideLabels = settings.hide_labels ?? defaultBoundaryTerritoryGroupSettings.hideLabels;

  return {
    allowDecimalRanges: settings.allow_decimal_ranges ?? defaultBoundaryTerritoryGroupSettings.allowDecimalRanges,
    boundariesAsTerritories: settings.boundaries_as_territories,
    boundaryFillType: settings.boundary_fill_type ?? defaultBoundaryTerritoryGroupSettings.boundaryFillType,
    boundaryStyle: new Map(settings.boundary_style?.map(item => ([item.boundaryId, item.style]))),
    boundaryTerritories,
    boundaryTerritoryType: settings.boundary_territory_type,
    bucketColumnId: settings.bucket_column_id ?? null,
    bucketType: settings.bucket_type ?? null,
    bucketDistribution: settings.bucket_distribution ?? 'equal-count',
    calculateBucketFunction: settings.calculate_bucket_function,
    demographicId: settings.demographic_id ?? null,
    displayName: settings.display_name,
    emptyIsZero: settings.empty_is_zero,
    metrics: settings.metrics?.map(createMetricFromResponse).filter((metric): metric is MetricModel => !!metric) ?? [],
    rangesCount: settings.ranges_count ?? null,
    style: createBoundaryTerritoryStyleFromResponse(settings.style),
    hideFillWhenInactive: settings.fade_when_inactive ?? defaultBoundaryTerritoryGroupSettings.hideFillWhenInactive,
    disableMapInteraction: settings.disable_map_interaction ?? defaultBoundaryTerritoryGroupSettings.disableMapInteraction,
    hideLabels,
    hideWithoutData: settings.hide_without_data ?? defaultBoundaryTerritoryGroupSettings.hideWithoutData,
    ignoreFilters: settings.ignore_filters ?? defaultBoundaryTerritoryGroupSettings.ignoreFilters,
    selectionEditPreferredBoundaryGroup: settings.selection_edit_preferred_boundary_group,
    defaultStatistics: settings.default_statistics?.map(item => createBoundaryTerritoryDefaultStatisticFromResponse(item)),
    labelValueConfig: settings.boundary_territory_label_config ? convertLabelConfigFromServerModel(settings.boundary_territory_label_config) : getDefaultLabelValueSettings({ hideLabels }),
    labelStyle: settings.boundary_territory_label_style ? convertLabelStyleFromServerModel(settings.boundary_territory_label_style) : defaultBoundaryTerritoryGroupSettings.labelStyle,
  };
};

const createBoundaryTerritoryDefaultStatisticFromResponse = (
  responseStatistics: BoundaryTerritoryGroupDefaultStatisticServerModel
): BoundaryTerritoryGroupDefaultStatistic => {
  if (responseStatistics.type === ChartMetricType.Demographic) {
    return {
      type: ChartMetricType.Demographic,
      metric: responseStatistics.metric,
    };
  }
  else {
    return {
      type: ChartMetricType.SpreadsheetColumn,
      metric: responseStatistics.metric,
      virtualSpreadsheetId: responseStatistics.virtual_spreadsheet_id,
    };
  }
};

const createBoundaryTerritoryDefaultStatisticRequest = (
  responseStatistics: BoundaryTerritoryGroupDefaultStatistic
): BoundaryTerritoryGroupDefaultStatisticServerModel => {
  if (responseStatistics.type === ChartMetricType.Demographic) {
    return {
      type: ChartMetricType.Demographic,
      metric: responseStatistics.metric,
    };
  }
  else {
    return {
      type: ChartMetricType.SpreadsheetColumn,
      metric: responseStatistics.metric,
      virtual_spreadsheet_id: responseStatistics.virtualSpreadsheetId,
    };
  }
};

export const createBoundaryTerritoryFromResponse = (
  response: BoundaryTerritoryResponse
): BoundaryTerritory => {
  if (response.custom) {
    return {
      boundaryTerritoryId: response.boundary_territory_id,
      displayName: response.display_name,
      custom: response.custom,
      translate: response.translate ?? false,
      style: response.style,
    };
  }
  return {
    boundaryTerritoryId: response.boundary_territory_id,
    displayName: response.display_name,
    bucket: response.bucket,
    custom: response.custom,
    style: response.style,
    translate: response.translate ?? false,
  };
};

export const createBoundaryTerritoryStyleRequest = (
  boundaryTerritoryStyle: BoundaryTerritoryGroupStyle
): BoundaryTerritoryStyleRequest => ({
  gradient_colors: boundaryTerritoryStyle.gradientColors,
  line_color: boundaryTerritoryStyle.lineColor,
  line_width: boundaryTerritoryStyle.lineWidth,
});

export const createBoundaryTerritoryStyleFromResponse = (
  response: BoundaryTerritoryStyleResponse
): BoundaryTerritoryGroupStyle => {
  return {
    gradientColors: {
      ...(response.gradient_colors),
      minimal: response.gradient_colors.minimal
        ?? boundaryTerritoryGroupCreateSettingsStyleDefaults.gradient_colors.minimal,
    },
    lineColor: response.line_color,
    lineWidth: response.line_width,
  };
};

const createBoundaryTerritoryRequest = (
  territory: BoundaryTerritory
): BoundaryTerritoryRequest => {
  const request: Mutable<BoundaryTerritoryRequest> = {
    boundary_territory_id: territory.boundaryTerritoryId,
    style: territory.style,
    custom: territory.custom,
    display_name: territory.displayName,
    translate: territory.translate,
  };

  if (!territory.custom) {
    request.bucket = territory.bucket;
  }

  return request;
};

const getBoundaryTerritorySettingsBoundaryStyleRequest = (boundaryStyle: ReadonlyMap<number, BoundaryTerritoryStyle>) => {
  return Array.from(boundaryStyle).map(([boundaryId, boundaryItemStyle]) => ({
    boundaryId,
    style: boundaryItemStyle,
  }));
};

export const createBoundaryTerritorySettingsRequestFromBoundaryTerritorySettings = (
  settings: Partial<BoundaryTerritoryGroupSettings> &
    Pick<BoundaryTerritoryGroupSettings, 'boundaryFillType' | 'boundaryTerritoryType'>,
): BoundaryTerritoryGroupSettingsRequest => {
  return {
    allow_decimal_ranges: settings.allowDecimalRanges,
    boundaries_as_territories: settings.boundariesAsTerritories,
    boundary_fill_type: settings.boundaryFillType,
    boundary_style: settings.boundaryStyle && getBoundaryTerritorySettingsBoundaryStyleRequest(settings.boundaryStyle),
    boundary_territories: settings.boundaryTerritories?.map(territory => createBoundaryTerritoryRequest(territory)),
    boundary_territory_type: settings.boundaryTerritoryType,
    bucket_column_id: settings.bucketColumnId,
    bucket_type: settings.bucketType,
    bucket_distribution: settings.bucketDistribution ?? 'equal-count',
    calculate_bucket_function: settings.calculateBucketFunction,
    demographic_id: settings.demographicId,
    metrics: settings.metrics?.map(createServerModelFromMetric)
      .filter((metric): metric is MetricServerModel => !!metric),
    ranges_count: settings.rangesCount ?? undefined,
    style: settings.style && createBoundaryTerritoryStyleRequest(settings.style),
    fade_when_inactive: settings.hideFillWhenInactive,
    disable_map_interaction: settings.disableMapInteraction,
    hide_labels: settings.hideLabels,
    hide_without_data: settings.hideWithoutData,
    ignore_filters: settings.ignoreFilters,
    selection_edit_preferred_boundary_group: settings.selectionEditPreferredBoundaryGroup,
    default_statistics: settings.defaultStatistics?.map(item => createBoundaryTerritoryDefaultStatisticRequest(item)),
    boundary_territory_label_style: convertLabelStyleToServerModel(settings.labelStyle ?? defaultBoundaryTerritoryGroupSettings.labelStyle),
    boundary_territory_label_config: convertLabelConfigToServerModel(settings.labelValueConfig ?? defaultBoundaryTerritoryGroupSettings.labelValueConfig),
  };
};

export const createBoundaryTerritoryCreateRequestFromBoundaryTerritoryGroupModel = (
  boundaryTerritoryGroup: BoundaryTerritoryGroupModel
): BoundaryTerritoryGroupCreateRequest => ({
  boundary_group_id: boundaryTerritoryGroup.boundaryGroupId,
  map_id: boundaryTerritoryGroup.mapId,
  matchings: getBoundaryTerritoryGroupMatchupRequest(boundaryTerritoryGroup.matchings),
  settings: createBoundaryTerritorySettingsRequestFromBoundaryTerritorySettings(boundaryTerritoryGroup.settings),
});

export const createBoundaryTerritoryConvertRequestFromBoundaryTerritoryGroupModel = (
  boundaryTerritoryGroup: BoundaryTerritoryGroupModel
): BoundaryTerritoryGroupConvertRequest => {
  const settingsWithoutNoDataBTs = {
    ...boundaryTerritoryGroup.settings,
    boundaryTerritories: boundaryTerritoryGroup.settings.boundaryTerritories
      .filter(bt => !noDataBoundaryTerritoryIds.includes(bt.boundaryTerritoryId)),
  };

  return ({
    boundary_territory_group_id: boundaryTerritoryGroup.boundaryTerritoryGroupId,
    boundary_group_id: boundaryTerritoryGroup.boundaryGroupId,
    map_id: boundaryTerritoryGroup.mapId,
    matchings: getBoundaryTerritoryGroupMatchupRequest(boundaryTerritoryGroup.matchings),
    settings: createBoundaryTerritorySettingsRequestFromBoundaryTerritorySettings(settingsWithoutNoDataBTs),
  });
};

export const createBoundaryTerritoryUpdateRequestFromBoundaryTerritoryGroup = (
  boundaryTerritoryGroup: BoundaryTerritoryGroup,
): BoundaryTerritoryGroupUpdateRequest => ({
  ...createBoundaryTerritoryCreateRequestFromBoundaryTerritoryGroupModel(boundaryTerritoryGroup),
  boundary_territory_group_id: boundaryTerritoryGroup.boundaryTerritoryGroupId,
  etag: boundaryTerritoryGroup.etag ? createEtagServerModel(boundaryTerritoryGroup.etag) : undefined,
});

export const convertBTGSettingsRequestToResponse = (
  requestSettings: BoundaryTerritoryGroupSettingsRequest,
  boundaryTerritories: BoundaryTerritoryResponse[],
): BoundaryTerritoryGroupSettingsResponse => {
  return {
    ...defaultBoundaryTerritoryGroupResponseSettings,
    style: boundaryTerritoryGroupCreateSettingsStyleDefaults,
    ...requestSettings,

    display_name: null,
    empty_is_zero: null,

    boundary_territories: boundaryTerritories,
    demographic_id: requestSettings.demographic_id ?? null,
    boundaries_as_territories: requestSettings.boundaries_as_territories ?? false,
    calculate_bucket_function: requestSettings?.calculate_bucket_function
      ?? getCalculateBucketFunction(requestSettings?.boundary_territory_type)
      ?? 'equal',
  };
};

export const convertBTGMatchupRequestToResponse = (
  matchupRequest?: BoundaryTerritoryGroupMatchingsRequest
): BoundaryTerritoryGroupMatchingsResponse => {
  return {
    matching_type: matchupRequest?.matching_type ?? BoundaryMatchingType.Geometry,
    match_columns: matchupRequest?.match_columns ?? [],
  };
};

export const sortBoundaryTerritoriesFromResponse = (a: BoundaryTerritory, b: BoundaryTerritory) =>
  a.custom && !b.custom ? 1 : !a.custom && b.custom ? -1 : 0;
